import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Fragment, memo, useCallback, useMemo } from 'react'
import { goToModelsYouMayLike } from '../../../../actions/nav'
import { MYML_LOCATIONS } from '../../../../mfcLogic/api/modelsYouMayLike'
import { isLounge } from '../../../../mfcLogic/userProperties/user'
import i18n from '../../../../utilities/i18n'
import Button from '../../../Buttons/Button'
import { useModelColor } from '../../../Hooks/useModelColor'
import useModelsYouMayLike from '../../../Hooks/useModelsYouMayLike'
import { useUserSelector } from '../../../Hooks/useUser'
import { getThemeSpacing } from '../../../styles/appTheme'
import APP_BREAKPOINTS, { maxWindowWidth } from '../../../styles/breakpoints'
import { StyledNoResults } from '../shared'
import Row from './EmbeddedRow'

const StyledRoot = styled('section')({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
})

const StyledResultRow = styled('article')({
    width: '100%',
})

// const loadingPaddingStyle = { padding: getThemeSpacing(2) }

const moreButtonStyles = css({
    marginTop: getThemeSpacing(0.5),
    fontWeight: 600,
    minWidth: '100%',
    [maxWindowWidth(APP_BREAKPOINTS.sm)]: {
        marginBottom: getThemeSpacing(1),
        fontSize: 12.5,
        lineHeight: 1.96,
    },
})

interface IYouMayLike {
    hideNoResultNotice?: boolean
    id: number
    maxResultCount?: number
    inRoomOrProfile?: boolean
}

const YouMayLike = memo((props: IYouMayLike) => {
    const {
        hideNoResultNotice,
        id,
        maxResultCount = 6,
        inRoomOrProfile = false,
    } = props
    const { modelResults } = useModelsYouMayLike(
        id,
        MYML_LOCATIONS.menu,
        inRoomOrProfile,
    )
    const modelColor = useUserSelector(id, 'color')
    const name = useUserSelector(id, 'name')
    const color = useModelColor(modelColor)
    const inLounge = isLounge(name)

    const topModels = useMemo(
        () => modelResults.slice(0, maxResultCount),
        [modelResults, maxResultCount],
    )

    const onOpenMoreModelsYouMayLike = useCallback(() => {
        goToModelsYouMayLike(name)
    }, [name])

    const Content = (() => {
        if (topModels.length === 0) {
            if (hideNoResultNotice) return <Fragment />
            return (
                <StyledNoResults data-testid="ModelsYouMayLike__NoResults">
                    <p>
                        {i18n.t('modelsYouMayLike.noRecommendations')}{' '}
                        <span style={{ color, fontWeight: 'bold' }}>
                            {name}
                        </span>
                        .
                    </p>
                </StyledNoResults>
            )
        }

        return (
            <>
                <StyledResultRow
                    key={id}
                    data-testid="ModelsYouMayLike__ResultRows"
                >
                    {topModels.map((model) => (
                        <div key={model.user_id}>
                            <Row
                                id={model.user_id}
                                recommendation={model.similarity}
                                inRoomOrProfile={inRoomOrProfile}
                            />
                        </div>
                    ))}
                </StyledResultRow>
                {!inLounge && (
                    <StyledResultRow>
                        <Button
                            data-testid="ModelsYouMayLike__Button"
                            onClick={onOpenMoreModelsYouMayLike}
                            css={moreButtonStyles}
                            size="medium"
                            border
                            clean
                        >
                            {i18n.t('modelsYouMayLike.moreModels')}
                        </Button>
                    </StyledResultRow>
                )}
            </>
        )
    })()

    return (
        <StyledRoot key={id} data-testid="ModelsYouMayLike__Container">
            {Content}
        </StyledRoot>
    )
})

export default memo(YouMayLike)
